import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'

import { Container, Overline, Skeleton } from '@/design-system/primitives'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { usePublicEvent } from '@/features/event/api'
import { NoAccess, StaffGate } from '@/lib/staffGate'
import { fetchMe } from '@/lib/staffSession'

/**
 * `/ops/caisse` — l'ancienne « tournée des espèces », remplacée.
 *
 * Cet écran listait toutes les commandes de table à encaisser, sans menu qui y
 * mène, et encaissait « au compte juste » : impossible d'y saisir le montant
 * reçu ni de changer de moyen de règlement. Tout ce qu'il faisait existe
 * désormais, en mieux, là où les gens travaillent :
 *
 * * le **serveur** encaisse ses tables (et celles sans serveur) depuis
 *   `/ops/service`, onglet « À encaisser » — montant reçu, monnaie, moyen ;
 * * le **bar** et la **cuisine** voient en tête de leur écran les commandes
 *   non réglées qui les concernent, et les encaissent dans la caisse du poste
 *   avec le même panneau ;
 * * la **porte** encaisse les commandes d'entrée au scan.
 *
 * Toute commande de table contient au moins une ligne de cuisine ou de bar :
 * aucune ne reste donc sans écran. Plutôt que de maintenir un troisième
 * panneau d'encaissement qui divergerait des deux autres, l'adresse — gardée
 * parce qu'elle a pu être notée ou mise en favori — renvoie chacun vers son
 * écran.
 */
export function CashPage() {
  const { t } = useTranslation()
  return (
    <StaffGate title={t('cashDesk.overline')}>
      {(_session, signOut) => <CashRedirect onSignOut={signOut} />}
    </StaffGate>
  )
}

/** Dans l'ordre : le geste d'encaisser à table d'abord, puis le poste. */
const DESTINATIONS: [permission: string, path: string][] = [
  ['service.order.read', '/ops/service'],
  ['bar.order.update_status', '/ops/bar'],
  ['kitchen.order.update_status', '/ops/cuisine'],
  ['checkin.scan.execute', '/ops/checkin'],
  ['pos.session.open', '/ops/pos'],
  ['pos.terminal.access', '/ops/pos'],
]

function CashRedirect({ onSignOut }: { onSignOut: () => void }) {
  const { t } = useTranslation()
  const eventQuery = usePublicEvent()
  const eventId = eventQuery.data?.id
  const me = useQuery({
    queryKey: ['staff-me', eventId],
    queryFn: async () => fetchMe(eventId),
    enabled: Boolean(eventId),
  })

  if (me.data) {
    const permissions = new Set(me.data.permissions)
    const target = DESTINATIONS.find(([permission]) => permissions.has(permission))
    if (target) return <Navigate replace to={target[1]} />
    return <NoAccess title={t('cashDesk.overline')} onSignOut={onSignOut} />
  }

  return (
    <Container className="max-w-md py-24">
      <div className="flex items-center justify-between gap-3">
        <Overline>{t('cashDesk.overline')}</Overline>
        <LanguageSwitcher />
      </div>
      <p className="mt-3 text-sm text-content-secondary">{t('cashDesk.moved')}</p>
      <Skeleton className="mt-6 h-10 w-full" />
    </Container>
  )
}
